import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Star, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';
import { AuthModal } from '../components/auth/AuthModal';
import { CardCustomizationModal } from '../components/shop/CardCustomizationModal';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ShopPage() {
  const navigate = useNavigate();
  const [reviewCards, setReviewCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardType, setSelectedCardType] = useState(null);
  const { isAuthenticated } = useAuth();
  const { formatPrice, selectedCountry } = useCountry();

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      // Fetch review standees (renamed from review cards)
      const standeeResponse = await apiRequest(`${BACKEND_URL}/api/admin/public/review-standees`, {
        method: 'GET',
      });

      // Fetch digital card categories
      const categoryResponse = await fetch(`${BACKEND_URL}/api/admin/public/card-categories`);
      const categoryData = await categoryResponse.json();

      if (standeeResponse.ok) {
        setReviewCards(standeeResponse.data.review_standees || []);
      }
      setCategories(categoryData.categories || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setLoading(false);
    }
  };

  const handleBuyReviewCard = (card) => {
    setSelectedCard(card);
    setSelectedCardType(card.name);
    if (isAuthenticated) {
      setShowCustomizationModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowCustomizationModal(true);
  };

  const handleCustomizationConfirm = (customData) => {
    setShowCustomizationModal(false);
    navigate('/checkout', {
      state: {
        card: selectedCard,
        cardType: selectedCardType,
        displayName: customData.displayName,
        logoPreview: customData.logoPreview,
      }
    });
  };

  const handleViewCategory = (categoryId) => {
    navigate(`/digital-cards/category/${categoryId}`);
  };

  // Color variations for categories
  const bgColors = [
    'bg-blue-100',
    'bg-pink-100', 
    'bg-yellow-100',
    'bg-purple-100',
    'bg-green-100',
    'bg-orange-100'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Review Standees
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            Review Standees
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Boost your business reputation with our Google and TripAdvisor review standees. Make it incredibly easy for customers to leave reviews.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-navy mx-auto"></div>
          <p className="text-gray-600 mt-4 text-lg">Loading products...</p>
        </div>
      ) : (
        <>
          {/* COMMENTED OUT - Digital Card Categories Section - Will be used later */}
          {/* {categories.length > 0 && (
            <section className="py-16 px-4 bg-white">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-4xl lg:text-5xl font-extrabold text-navy mb-4">
                    Digital Business Cards
                  </h2>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Your professional identity in one tap. Modern NFC business cards for the digital age.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {categories.map((category, index) => (
                    <div 
                      key={category.id} 
                      className={`${bgColors[index % bgColors.length]} rounded-[2.5rem] p-8 relative overflow-hidden min-h-[500px] flex flex-col hover:shadow-xl transition-shadow duration-300`}
                    >
                      <div className="flex-1">
                        <h3 className="text-3xl font-bold text-navy mb-3">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-700 mb-6">
                          {category.description}
                        </p>

                        {category.image && (
                          <div className="mb-6 flex justify-center items-center h-40">
                            <img 
                              src={`${BACKEND_URL}${category.image}`}
                              alt={category.name}
                              className="rounded-xl shadow-lg object-cover max-h-40"
                            />
                          </div>
                        )}

                        {category.features && category.features.length > 0 && (
                          <ul className="space-y-3 mb-6">
                            {category.features.slice(0, 3).map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-gray-700">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="mt-auto pt-4">
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-sm text-gray-600">Starting from</span>
                          <span className="text-4xl font-bold text-navy">
                            ${category.starting_from}
                          </span>
                        </div>
                        <Button 
                          onClick={() => handleViewCategory(category.id)}
                          className="w-full bg-navy text-white hover:bg-navy/90 py-6 rounded-2xl text-lg font-semibold flex items-center justify-center gap-2"
                        >
                          View All Cards
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )} */}

          {/* Review Standees Section */}
          {reviewCards.length > 0 && (
            <section className="py-16 px-4 bg-gradient-to-br from-[#9B7EFF] to-[#7D65FA]">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
                    Our Review Standees
                  </h2>
                  <p className="text-lg text-purple-100 max-w-2xl mx-auto">
                    Choose from Google or TripAdvisor review standees to boost your online reputation
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {reviewCards.map((card) => (
                    <div 
                      key={card.id} 
                      className="bg-dark-gray rounded-[2.5rem] p-8 text-white relative overflow-hidden flex flex-col hover:shadow-2xl transition-shadow duration-300"
                    >
                      {/* Sample Design Image */}
                      {card.sample_design_url && (
                        <div className="mb-6 -mx-8 -mt-8">
                          <img
                            src={card.sample_design_url}
                            alt={card.name}
                            className="w-full h-48 object-cover"
                          />
                        </div>
                      )}

                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-4">
                          {card.name}
                        </h3>
                        
                        {/* Short Description */}
                        {card.short_description && (
                          <p className="text-gray-300 mb-6">
                            {card.short_description}
                          </p>
                        )}
                        
                        {/* Features */}
                        {card.features && card.features.length > 0 && (
                          <ul className="space-y-2 mb-6">
                            {card.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0 mt-1" />
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Materials & Pricing */}
                        {card.materials && card.materials.length > 0 && (
                          <div className="mb-6">
                            <p className="text-xs text-gray-400 mb-2">Available Materials:</p>
                            <div className="space-y-1">
                              {card.materials.map((material) => (
                                <div key={material.id} className="flex justify-between text-sm">
                                  <span>{material.name}</span>
                                  <span className="font-semibold">{formatPrice(material.price)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Buy Button */}
                      <Button 
                        onClick={() => handleBuyReviewCard(card)}
                        className="bg-white text-navy hover:bg-gray-100 rounded-full px-8 py-4 text-base font-semibold w-full mt-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Empty State */}
          {categories.length === 0 && reviewCards.length === 0 && (
            <section className="py-20 px-4">
              <div className="max-w-2xl mx-auto text-center">
                <p className="text-gray-600 text-lg">No products available at the moment. Please check back later.</p>
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-navy mb-6">
            Need Help Choosing?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Not sure which review standee is right for your business? Our team is here to help you find the perfect solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-6 text-base font-semibold"
            >
              Contact Us
            </Button>
            <Button 
              onClick={() => navigate('/review-cards')}
              variant="outline" 
              className="border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full px-8 py-6 text-base font-semibold"
            >
              Learn More About Review Standees
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Modals */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      
      <CardCustomizationModal 
        isOpen={showCustomizationModal}
        onClose={() => setShowCustomizationModal(false)}
        cardType={selectedCardType}
        card={selectedCard}
        productImage={selectedCard?.sample_design_url}
        onConfirm={handleCustomizationConfirm}
      />
    </div>
  );
}
